import { DialogComponent } from '@theme/dialog';

/**
 * @typedef {object} InstagramFeedMediaGridPayload
 * @property {string} image_url
 * @property {string} image_alt
 * @property {string} title
 * @property {string} description_html
 * @property {string} product_url
 * @property {string} product_title
 * @property {string} product_price
 * @property {{x:number,y:number,product_url?:string,product_title?:string,product_price?:string}[]} hotspots
 */

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLImageElement} modalImage
 * @property {HTMLElement} modalTitle
 * @property {HTMLElement} modalDescription
 * @property {HTMLAnchorElement} modalBuyButton
 * @property {HTMLElement} modalHotspots
 */

export class InstagramFeedMediaGridComponent extends DialogComponent {
  /** @type {string[]} */
  requiredRefs = ['dialog', 'modalImage', 'modalTitle', 'modalDescription', 'modalBuyButton', 'modalHotspots'];

  connectedCallback() {
    super.connectedCallback();
  }

  /** @param {Event} event */
  openFromTrigger = (event) => {
    event.preventDefault();

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const trigger = target.matches('[data-payload]') ? target : target.closest('[data-payload]');
    if (!(trigger instanceof HTMLElement)) return;

    const payload = this.#parsePayload(trigger.dataset.payload);
    if (!payload) {
      return;
    }

    this.#render(payload);
    this.showDialog();
  };

  /** @param {string | undefined} raw */
  #parsePayload(raw) {
    if (!raw) return null;

    try {
      // `data-payload` is HTML-escaped in Liquid; dataset gives us decoded string.
      const parsed = /** @type {InstagramFeedMediaGridPayload} */ (JSON.parse(raw));
      return parsed;
    } catch (error) {
      return null;
    }
  }

  /** @param {InstagramFeedMediaGridPayload} payload */
  #render(payload) {
    const { modalImage, modalTitle, modalDescription, modalBuyButton, modalHotspots } = this.refs;

    if (payload.image_url) {
      modalImage.src = payload.image_url;
      modalImage.alt = payload.image_alt || '';
      modalImage.removeAttribute('hidden');
    } else {
      modalImage.src = '';
      modalImage.alt = '';
      modalImage.setAttribute('hidden', '');
    }

    modalTitle.textContent = payload.title || '';
    modalDescription.innerHTML = payload.description_html || '';

    if (payload.product_url) {
      modalBuyButton.href = payload.product_url;
      modalBuyButton.hidden = false;
    } else {
      modalBuyButton.hidden = true;
      modalBuyButton.removeAttribute('href');
    }

    while (modalHotspots.firstChild) modalHotspots.removeChild(modalHotspots.firstChild);

    if (!Array.isArray(payload.hotspots) || !payload.hotspots.length) {
      return;
    }

    const fallbackUrl = payload.product_url || '';
    const fallbackTitle = payload.product_title || '';
    const fallbackPriceText = this.#htmlToText(payload.product_price || '');

    for (const hotspot of payload.hotspots) {
      if (!hotspot || typeof hotspot.x !== 'number' || typeof hotspot.y !== 'number') continue;

      const url = hotspot.product_url || fallbackUrl;
      if (!url) continue;

      const link = document.createElement('a');
      link.className = 'instagram-media-modal__hotspot';
      link.href = url;
      link.style.left = `${Math.max(0, Math.min(100, hotspot.x))}%`;
      link.style.top = `${Math.max(0, Math.min(100, hotspot.y))}%`;

      const tooltip = document.createElement('span');
      tooltip.className = 'instagram-media-modal__hotspot-tooltip';

      const title = hotspot.product_title || fallbackTitle;
      const price = this.#htmlToText(hotspot.product_price || '') || fallbackPriceText;
      tooltip.textContent = title && price ? `${title} — ${price}` : title || price;

      link.appendChild(tooltip);
      modalHotspots.appendChild(link);
    }
  }

  /** @param {string} value */
  #htmlToText(value) {
    if (!value) return '';
    if (!value.includes('<')) return value;

    try {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = value;
      return (wrapper.textContent || '').trim();
    } catch {
      return value.replace(/<[^>]*>/g, '').trim();
    }
  }
}

if (!customElements.get('instagram-feed-media-grid-component')) {
  customElements.define('instagram-feed-media-grid-component', InstagramFeedMediaGridComponent);
}
